import { createFileRoute } from "@tanstack/react-router";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { TrustBand } from "@/components/site/TrustBand";
import { LeadMagnetForm } from "@/components/site/LeadMagnetForm";
import { BrevetSignup } from "@/components/site/BrevetSignup";
import { FormatShowcase } from "@/components/site/FormatShowcase";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { Reveal } from "@/components/site/wow/Reveal";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { TiltCard } from "@/components/site/wow/TiltCard";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import { StatHero } from "@/components/site/wow/StatHero";
import { ThumbStopMoment } from "@/components/site/wow/ThumbStopMoment";
import { LiveCountUp } from "@/components/site/case/LiveCountUp";
import { TrendingUp, Target, Sparkles, ShoppingCart, Users, GraduationCap } from "lucide-react";
import { featuredCase } from "@/content/cases";
import { AnswerFaq } from "@/components/site/aeo/AnswerFaq";
import { toFaqJsonLd } from "@/lib/aeo/answers";
import { aeoGeo } from "@/lib/seo/schema";

const VARFOR_FILM_FAQ_IDS = [
  "sv_varfor_lopande_film",
  "sv_hur_anvanda_video_b2b",
  "sv_vilka_filmformat_for_sociala_medier",
];

export const Route = createFileRoute("/varfor-film")({
  head: () => ({
    meta: [
      { title: "Varför film? — Affärsnyttan med rörligt innehåll | Fenny" },
      { name: "description", content: "Plattformsstatistik, neurodesign och praktiska argument för varför rörligt innehåll driver tillväxt, konvertering och varumärkesstyrka 2025." },
      { property: "og:title", content: "Varför film? — Affärsnyttan | Fenny" },
      { property: "og:description", content: "Tillväxt, konvertering, försäljning och varumärke — så bygger film affär 2025." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/varfor-film" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Varför film? — Fenny" },
      { name: "twitter:description", content: "Så bygger film affär 2025." },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/varfor-film" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/varfor-film", name: "Varför film? — Affärsnyttan med rörligt innehåll | Fenny", description: "Plattformsstatistik, neurodesign och praktiska argument för varför rörligt innehåll driver tillväxt, konvertering och varumärkesstyrka 2025."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(toFaqJsonLd(VARFOR_FILM_FAQ_IDS)),
      },
    ],
  }),
  component: VarforFilmPage,
});

const reasons = [
  { icon: TrendingUp, title: "Tillväxt", body: "Rörligt är det enda formatet som plattformarna prioriterar i sina algoritmer just nu. Mer organisk räckvidd, lägre CPM, snabbare lärande.", example: "Klient: +287k organiska views på en singel-produktion." },
  { icon: Target, title: "Konvertering", body: "Video på landningssidor lyfter konverteringar dramatiskt — för att de besvarar invändningar och skapar förtroende på sekunder.", example: "Wyzowl 2024: produktvideo lyfter konvertering 80%." },
  { icon: Sparkles, title: "Varumärke", body: "Få format bygger känsla, ton och igenkänning som film. Återkommande publik utvecklar en relation till ert varumärke — inte bara er produkt.", example: "30 år med Garageportexperten — visad i 1 år efter premiär." },
  { icon: ShoppingCart, title: "Försäljning", body: "Sales enablement-klipp, demo-loops, kundberättelser. Säljteamet får ammunition som faktiskt landar i pipeline-möten.", example: "B2B: 67% kortare säljcykler med video i pitchen." },
  { icon: Users, title: "Rekrytering", body: "Människor söker till kulturer, inte annonser. Film visar varför man ska jobba hos er på 30 sekunder — bättre än någon karriärsida.", example: "Garageportexperten: +38% ansökningar på 90 dagar." },
  { icon: GraduationCap, title: "Internt", body: "Onboarding, ledningskommunikation och kunskapsöverföring — film skär ner förståelse-tiden och funkar lika bra på distans som i lunchrummet.", example: "Snitt: 50% snabbare onboarding med videostöd." },
];

function VarforFilmPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative isolate overflow-hidden pt-40 pb-28 md:pt-52 md:pb-36">
        <GrainOverlay opacity={0.06} />
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,color-mix(in_oklab,var(--peach)_12%,transparent)_0%,transparent_55%)]" />
        <div className="container-wide">
          <div className="grid items-end gap-12 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <Reveal>
                <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-peach/30 bg-peach/[0.06] px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-peach">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-peach opacity-60" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-peach" />
                  </span>
                  Varför film?
                </div>
              </Reveal>
              <KineticHeading
                text="Film är inte en kanal. Det är ett affärsverktyg."
                className="text-5xl font-semibold text-foreground md:text-7xl lg:text-[5.25rem]"
                highlight={{ indices: [7, 8], className: "text-peach" }}
              />
              <Reveal delay={0.4}>
                <p className="mt-8 max-w-2xl text-lg text-muted-foreground md:text-xl">
                  Konsumenter, beslutsfattare och algoritmer har redan gått över till rörligt.
                  Frågan är inte om ni ska investera i film — utan hur ni gör det smart, löpande och affärsdrivet.
                </p>
              </Reveal>
              <Reveal delay={0.55}>
                <div className="mt-10 flex flex-wrap gap-3">
                  <Magnetic>
                    <Button to="/kontakt" variant="peach" arrow trackId="book_strategy_meeting" trackProps={{ location: "varfor_film_hero" }}>
                      Boka strategimöte
                    </Button>
                  </Magnetic>
                  <Button to="/kunskap" variant="outline">Läs i kunskapshubben</Button>
                </div>
              </Reveal>
            </div>

            {/* Live ticker card */}
            <Reveal delay={0.5}>
              <div className="rounded-3xl border border-white/10 bg-surface/60 p-7">
                <div className="text-[11px] uppercase tracking-[0.18em] text-peach">Just nu</div>
                <div className="mt-4 font-serif text-5xl font-semibold leading-none text-foreground">
                  <LiveCountUp value={1247318} />
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  timmar video tittas på i Sverige denna timme. Var är ert innehåll i flödet?
                </p>
                <div className="mt-6 border-t border-white/10 pt-4 text-[11px] uppercase tracking-widest text-muted-foreground">
                  Källa · Statista 2025
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Animated stat hero */}
      <Section className="bg-surface/30">
        <EyebrowHeading
          eyebrow="Datan ni inte kan ignorera"
          title="Fyra siffror — fyra beslut."
          lede="Varje siffra är hämtad från en seriös källa och leder till ett konkret val för 2026."
        />
        <div className="mt-14">
          <StatHero />
        </div>
      </Section>

      {/* Neurodesign / Thumb-stop */}
      <Section>
        <div className="mx-auto max-w-5xl">
          <EyebrowHeading
            eyebrow="Neurodesign i praktiken"
            title="Tummen bestämmer på 1,7 sekunder."
            lede="Vi kan beskriva det. Eller så får ni känna det själva."
            align="center"
          />
          <div className="mt-12">
            <ThumbStopMoment />
          </div>
        </div>
      </Section>

      {/* Six reasons */}
      <Section className="bg-surface/40">
        <EyebrowHeading
          eyebrow="Sex affärsskäl"
          title="Vad film faktiskt levererar."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {reasons.map((r) => {
            const Icon = r.icon;
            return (
              <TiltCard key={r.title} glow="peach" className="rounded-3xl">
                <article className="flex h-full flex-col rounded-3xl border border-white/10 bg-background p-7">
                  <Icon className="h-6 w-6 text-peach" strokeWidth={1.5} />
                  <h3 className="mt-5 text-xl font-semibold">{r.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{r.body}</p>
                  <div className="mt-auto pt-5 text-xs italic text-foreground/70">{r.example}</div>
                </article>
              </TiltCard>
            );
          })}
        </div>
      </Section>

      {/* Format mirror */}
      <Section>
        <EyebrowHeading
          eyebrow="Samma berättelse"
          title="Olika kanaler. Samma idé."
          lede="Ett klipp som regisseras rätt skär ner till tio versioner — utan att tappa själen."
          align="center"
        />
        <div className="mt-12">
          <FormatShowcase />
        </div>
      </Section>

      {/* Cinematic quote */}
      <Section className="relative isolate overflow-hidden bg-black">
        <GrainOverlay opacity={0.14} />
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,color-mix(in_oklab,var(--butter)_10%,transparent)_0%,transparent_60%)]" />
        <Reveal>
          <figure className="mx-auto max-w-4xl text-center">
            <div className="text-[11px] uppercase tracking-[0.2em] text-butter/80">Kundröst</div>
            <blockquote className="mt-6 font-serif text-3xl leading-tight text-butter md:text-5xl lg:text-6xl">
              &ldquo;{featuredCase.quote?.text}&rdquo;
            </blockquote>
            <figcaption className="mt-8 text-sm uppercase tracking-[0.18em] text-foreground/70">
              {featuredCase.quote?.name} · {featuredCase.quote?.role}
            </figcaption>
          </figure>
        </Reveal>
      </Section>

      {/* Sources */}
      <Section className="py-12 md:py-16">
        <div className="mx-auto max-w-4xl rounded-3xl border border-white/10 bg-surface/40 p-7">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Källförteckning</div>
          <ul className="mt-4 grid gap-2 text-sm text-foreground/80 sm:grid-cols-2">
            <li>· Cisco / Statista — Video traffic forecast 2025</li>
            <li>· Meta — Mobile Feed Attention Study</li>
            <li>· LinkedIn Business Marketing Report 2024</li>
            <li>· Wyzowl — Video Marketing Statistics 2024</li>
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">
            Vi länkar gärna originalrapporterna på begäran. Inga &ldquo;trust me, bro&rdquo;-statistik.
          </p>
        </div>
      </Section>

      {/* Lead magnet */}
      <Section>
        <div className="mx-auto max-w-4xl">
          <div className="mb-8 max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.18em] text-mint">Hela rapporten</div>
            <h2 className="mt-2 text-3xl font-semibold md:text-4xl">Vill ni ha siffrorna i pdf?</h2>
          </div>
          <LeadMagnetForm location="varfor_film_lead_magnet" />
        </div>
      </Section>

      {/* Brevet */}
      <Section className="bg-surface/40">
        <div className="mx-auto max-w-4xl">
          <BrevetSignup variant="block" location="varfor_film" />
        </div>
      </Section>

      <AnswerFaq
        answerIds={VARFOR_FILM_FAQ_IDS}
        eyebrow="Frågor & svar"
        title="Vanliga frågor om film som affärsverktyg"
      />


      <TrustBand ctaLocation="varfor_film_trust_band" />
    </div>
  );
}
